import React from 'react';

import { QueryInstanceSearchOptions } from '@lumapps/instance/api/types';
import { useQueryAdministrableInstances } from '@lumapps/instance/hooks/useQueryAdministrableInstances';
import { useQueryInstanceSearch } from '@lumapps/instance/hooks/useQueryInstanceSearch';

const MAX_RESULTS = 20;

/**
 * Hook that returns the different options needed for listing instances in the picker field
 */
export const useInstancePickerState = (
    params?: Partial<QueryInstanceSearchOptions>,
    options?: { instancesToExclude?: string[]; searchOnlyAdministrableSites?: boolean },
) => {
    const { instancesToExclude, searchOnlyAdministrableSites = false } = options || {};
    const [searchValue, setSearchValue] = React.useState<string | undefined>();
    const [enabled, setEnabled] = React.useState(false);
    const query = useQueryInstanceSearch(
        {
            userFavoritesOnly: false,
            maxResults: MAX_RESULTS,
            sortOrder: 'name',
            ...params,
            name: searchValue || undefined,
        },
        { enabled: enabled && !searchOnlyAdministrableSites },
    );

    const queryAdministrableSites = useQueryAdministrableInstances(
        {
            userFavoritesOnly: false,
            maxResults: MAX_RESULTS,
            sortOrder: 'name',
            ...params,
            name: searchValue || undefined,
        },
        { enabled: enabled && searchOnlyAdministrableSites },
    );

    const items = !searchOnlyAdministrableSites ? query.items : queryAdministrableSites.items;
    const loadingStatus = !searchOnlyAdministrableSites
        ? query.baseLoadingStatus
        : queryAdministrableSites.baseLoadingStatus;
    const onInfiniteScroll = !searchOnlyAdministrableSites
        ? () => query.hasNextPage && query.fetchNextPage()
        : () => queryAdministrableSites.hasNextPage && queryAdministrableSites.fetchNextPage();

    /**
     * Remove the ids from the instances passed in `instancesToExclude` and return the actually filtered list.
     */
    const filteredSites = React.useMemo(() => {
        if (instancesToExclude && instancesToExclude.length > 0) {
            return items.filter((site) => !instancesToExclude.includes(site.id));
        }
        return items;
    }, [instancesToExclude, items]);

    const onSearch = (val: string) => {
        setSearchValue(val);
    };

    const onBlur = () => {
        setSearchValue(undefined);
    };

    return {
        sites: filteredSites,
        status: loadingStatus,
        onSearch,
        onInfiniteScroll,
        onFocus: () => setEnabled(true),
        onBlur,
    };
};
